// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { formatUsTimestamp } from '../../api/format'
import { Database, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import { friendlyError } from '../../api/errors'

// D-UI-2: what each column shows, for its header funnel.
const PIONEER_FUNNELS: FunnelColumn<api.PioneerRxEvent>[] = [
  { key: 'when',    label: 'When',            value: e => e.parsed_at },
  { key: 'patient', label: 'Patient',         value: e => [e.patient_first_name, e.patient_last_name].filter(Boolean).join(' ') },
  { key: 'rx',      label: 'Rx #',            value: e => e.rx_number },
  { key: 'drug',    label: 'Drug',            value: e => e.dispensed_drug_name || e.prescribed_drug_name },
  { key: 'status',  label: 'Status',          value: e => e.current_rx_status_text },
  { key: 'tx',      label: 'Tx Status',       value: e => e.current_rx_tx_status_text },
  { key: 'payor',   label: 'Payor',           value: e => e.primary_payor_name ?? 'Cash' },
  { key: 'total',   label: 'Total / Patient', value: e => e.total_price_paid != null ? Number(e.total_price_paid) : null },
]

/**
 * /admin/pioneerrx — read-only view of the most recent PioneerRx events
 * received by the ingest webhook. Used by ops + compliance to verify that
 * sync is healthy and see what's flowing through.
 *
 * Backend: GET /api/pioneerrx/events?limit=N (admin-only).
 */
export default function PioneerRxTab() {
  const [items, setItems] = useState<api.PioneerRxEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [limit, setLimit] = useState<number>(50)

  async function reload() {
    setLoading(true); setError(null)
    try {
      const r = await api.listPioneerRxEvents(limit)
      setItems(r.items)
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { reload() }, [limit])

  // Reset to page 1 when the fetch limit changes (a length resetKey misses two
  // different result sets that happen to have the same row count).
  // The header funnels filter the whole loaded set; paging happens after.
  const fx = useTableFunnels(items, PIONEER_FUNNELS)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${limit}|${fx.sig}`)

  return (
    <div>
      <div style={headerRowStyle}>
        <div>
          <h2 style={h2Style}><Database size={16} aria-hidden="true" /> PioneerRx event stream</h2>
          <p style={subtitleStyle}>
            Recent events received from PioneerRx via the ingest webhook. PioneerRx is the
            system of record for Rx state (ADR-014); these are the source rows that update
            our mirror.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={selectLabelStyle}>
            Show
            <select value={limit} onChange={e => setLimit(Number(e.target.value))} style={selectStyle}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
          <button onClick={reload} disabled={loading} style={refreshBtn}>
            {loading ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
            Refresh
          </button>
        </div>
      </div>

      {error && <div style={errorBox}><AlertCircle size={14} /> {error}</div>}

      {loading && items.length === 0 ? (
        <div style={loadingBox}><Loader2 size={16} className="spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div style={emptyBox}>
          <Database size={28} style={{ color: 'var(--color-text-mute)' }} />
          <h3 style={{ fontSize: 16, margin: '8px 0 4px' }}>No events received yet</h3>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-soft)' }}>
            Once PioneerRx posts to the ingest webhook, events will appear here.
          </p>
        </div>
      ) : (
        <>
        {fx.chips}
        <div style={cardStyle}>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="events" />
          <table {...fx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>When {fx.funnel('when')}</th>
                <th style={thStyle}>Patient {fx.funnel('patient')}</th>
                <th style={thStyle}>Rx # {fx.funnel('rx')}</th>
                <th style={thStyle}>Drug {fx.funnel('drug')}</th>
                <th style={thStyle}>Status {fx.funnel('status')}</th>
                <th style={thStyle}>Tx Status {fx.funnel('tx')}</th>
                <th style={thStyle}>Payor {fx.funnel('payor')}</th>
                <th style={thStyle}>Total / Patient {fx.funnel('total')}</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(e => (
                <tr key={e.id} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={{ fontSize: 12 }}>{formatTime(e.parsed_at)}</div>
                    {e.message_id && (
                      <div style={mutedSmall} title={e.message_id}>
                        msg: {e.message_id.length > 12 ? e.message_id.slice(0, 12) + '…' : e.message_id}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {[e.patient_first_name, e.patient_last_name].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>
                    {e.rx_number ?? '—'}
                    {e.refill_number != null && e.refill_number > 0 && (
                      <span style={mutedSmall}> rf{e.refill_number}</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <div>{e.dispensed_drug_name || e.prescribed_drug_name || '—'}</div>
                    {e.is_substitution && <span style={subBadge}>SUB</span>}
                  </td>
                  <td style={tdStyle}>{e.current_rx_status_text ?? '—'}</td>
                  <td style={tdStyle}>{e.current_rx_tx_status_text ?? '—'}</td>
                  <td style={tdStyle}>{e.primary_payor_name ?? <span style={mutedSmall}>Cash</span>}</td>
                  <td style={tdStyle}>
                    {e.total_price_paid != null ? `$${Number(e.total_price_paid).toFixed(2)}` : '—'}
                    {e.patient_paid_amount != null && (
                      <div style={mutedSmall}>pt: ${Number(e.patient_paid_amount).toFixed(2)}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="events" />
        </div>
        </>
      )}
    </div>
  )
}

function formatTime(s: string): string {
  return formatUsTimestamp(s)
}

const headerRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  gap: 16, marginBottom: 16, flexWrap: 'wrap',
}
const h2Style: React.CSSProperties = { fontSize: 18, fontWeight: 600, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }
const subtitleStyle: React.CSSProperties = { margin: '4px 0 0', color: 'var(--color-text-soft)', fontSize: 13, maxWidth: 600 }
const selectLabelStyle: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-soft)', display: 'inline-flex', gap: 6, alignItems: 'center' }
const selectStyle: React.CSSProperties = {
  padding: '4px 8px', fontSize: 12, border: '1px solid var(--color-border)',
  borderRadius: 4, background: 'var(--color-surface)', fontFamily: 'inherit',
}
const refreshBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', fontSize: 12, fontWeight: 500,
  background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 4, color: 'var(--color-text-soft)', cursor: 'pointer', fontFamily: 'inherit',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'auto',
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = {
  textAlign: 'left' as const, padding: '10px 14px',
  background: 'var(--color-surface-alt)', borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' as const,
  color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = { borderBottom: '1px solid var(--color-border)' }
const tdStyle: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'middle' as const }
const mutedSmall: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const subBadge: React.CSSProperties = {
  marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
  padding: '1px 5px', background: '#fef3c7', color: '#854d0e', borderRadius: 3,
}
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 8,
}
const loadingBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: 24, color: 'var(--color-text-soft)', fontSize: 14 }
const emptyBox: React.CSSProperties = {
  padding: 32, textAlign: 'center' as const,
  background: 'var(--color-surface, #fff)', border: '1px dashed var(--color-border)', borderRadius: 8,
}
